import { useEffect, useMemo, useState } from 'react';
import { useStore } from 'zustand';

import type { GraphDocument } from '../../../core/entities/graph-document';
import type { JourneyNode, NodeType } from '../../../core/entities/node';
import { getWorkspaceEditLock, type WorkspaceEditLockMap } from '../../../core/policies/workspace/edit-lock';
import type { WorkspaceMotionProfile } from '../../../core/policies/workspace/visual-theme-profile';
import {
	deriveOutlineRows,
	filterAndSortOutlineRows,
	groupOutlineRows,
} from '../../../core/policies/workspace/derive-outline-rows';
import type { WorkspaceGraphMode } from '../../../core/ports/preferences-repository';
import { useLocale } from '../../preferences/i18n/LocaleProvider';
import { createOutlineWorkspaceStore } from '../services/outline-workspace-store';
import { OutlineWorkspaceTable } from './OutlineWorkspaceTable';
import { OutlineWorkspaceToolbar } from './OutlineWorkspaceToolbar';

export function OutlineWorkspace({
	activeEditLocks,
	currentGraphMode,
	focusedNodeId,
	graph,
	onBeginInlineEdit,
	onCancelInlineEdit,
	onCreate,
	onEdit,
	onLocate,
	onRequestDelete,
	onReturnToGraph,
	onSaveInlineEdit,
	open,
	readonly,
	createDisabled = false,
	createDisabledReason = '',
	motionProfile = 'default',
	themeProfileId = 'dark-cosmic-editorial',
	visibleType,
}: {
	activeEditLocks: WorkspaceEditLockMap;
	currentGraphMode: WorkspaceGraphMode;
	focusedNodeId: string | null;
	graph: GraphDocument;
	onBeginInlineEdit: (nodeId: string) => boolean;
	onCancelInlineEdit: (nodeId: string) => void;
	onCreate: (type: NodeType) => void;
	onEdit: (node: JourneyNode) => void;
	onLocate: (nodeId: string, nodeType: NodeType) => void;
	onRequestDelete: (node: JourneyNode) => void;
	onReturnToGraph: () => void;
	onSaveInlineEdit: (node: JourneyNode) => void;
	open: boolean;
	readonly: boolean;
	createDisabled?: boolean;
	createDisabledReason?: string;
	motionProfile?: WorkspaceMotionProfile;
	themeProfileId?: string;
	visibleType: NodeType | 'All';
}) {
	const { locale, t } = useLocale();
	const [outlineStore] = useState(() => createOutlineWorkspaceStore());
	const [hasOpened, setHasOpened] = useState(false);
 	const {
		collapsedGroupIds,
		groupBy,
		inlineDraft,
		inlineEditingNodeId,
		localVisibleTypes,
		searchQuery,
		clearInlineEdit,
		seedFromVisibleType,
		setSearchQuery,
		setSortDirection,
		setSortKey,
		sortDirection,
		sortKey,
		startInlineEdit,
		toggleCollapsedGroup,
		toggleGroupByType,
		toggleVisibleType,
		updateInlineDraft,
	} = useStore(outlineStore, (state) => state);

	useEffect(() => {
		if (open && !hasOpened) {
			seedFromVisibleType(visibleType);
			setHasOpened(true);
		}
	}, [hasOpened, open, seedFromVisibleType, visibleType]);

	useEffect(() => {
		if (!inlineEditingNodeId) {
			return;
		}

		if (graph.nodes.some((node) => node.nodeId === inlineEditingNodeId)) {
			return;
		}

		onCancelInlineEdit(inlineEditingNodeId);
		clearInlineEdit();
	}, [clearInlineEdit, graph.nodes, inlineEditingNodeId, onCancelInlineEdit]);

	const rows = useMemo(() => deriveOutlineRows(graph), [graph]);

	const visibleRows = useMemo(() => {
		return filterAndSortOutlineRows(rows, {
			searchQuery,
			localVisibleTypes,
			sortKey,
			sortDirection,
		});
	}, [localVisibleTypes, rows, searchQuery, sortDirection, sortKey]);

	const groups = useMemo(() => {
		return groupOutlineRows(visibleRows, groupBy);
	}, [groupBy, visibleRows]);

	function beginInlineEdit(node: JourneyNode) {
		if (readonly) {
			return;
		}

		if (!onBeginInlineEdit(node.nodeId)) {
			return;
		}

		startInlineEdit(node);
	}

	function handleCreate(type: NodeType) {
		if (readonly || createDisabled) {
			return;
		}

		onCreate(type);
	}

	function cancelInlineEdit() {
		if (!inlineEditingNodeId) {
			return;
		}

		onCancelInlineEdit(inlineEditingNodeId);
		clearInlineEdit();
	}

	function saveInlineEdit() {
		if (!inlineDraft) {
			return;
		}

		onSaveInlineEdit(inlineDraft);
		clearInlineEdit();
	}

	function openInlineDraftInFullEditor() {
		if (!inlineEditingNodeId || !inlineDraft) {
			return;
		}

		onCancelInlineEdit(inlineEditingNodeId);
		clearInlineEdit();
		onEdit(inlineDraft);
	}

	return (
		<section
			aria-label={t('workspace.outline.title')}
			className="floating-panel workspace-panel workspace-panel--outline"
			data-motion-profile={motionProfile}
			data-theme-profile={themeProfileId}
			hidden={!open}
			role="region"
		>
			{open ? (
				<>
					<OutlineWorkspaceToolbar
						currentGraphMode={currentGraphMode}
						createDisabled={createDisabled}
						createDisabledReason={createDisabledReason}
						groupByType={groupBy === 'type'}
						localVisibleTypes={localVisibleTypes}
						onCreate={handleCreate}
						onReturnToGraph={onReturnToGraph}
						onSearchQueryChange={setSearchQuery}
						onSortDirectionChange={setSortDirection}
						onSortKeyChange={setSortKey}
						onToggleGroupByType={toggleGroupByType}
						onToggleVisibleType={toggleVisibleType}
						readonly={readonly}
						searchQuery={searchQuery}
						sortDirection={sortDirection}
						sortKey={sortKey}
						totalCount={graph.nodes.length}
						visibleCount={visibleRows.length}
					/>
					{groups.length === 0 ? (
						<div className="panel-card">
							<h3>{t('workspace.outline.emptyTitle')}</h3>
							<p className="workspace-subtitle">{t('workspace.outline.emptyBody')}</p>
						</div>
					) : (
						<OutlineWorkspaceTable
							activeEditLocks={activeEditLocks}
							collapsedGroupIds={collapsedGroupIds}
							focusedNodeId={focusedNodeId}
							groups={groups}
							inlineDraft={inlineDraft}
							inlineEditingNodeId={inlineEditingNodeId}
							locale={locale}
							onCancelInlineEdit={cancelInlineEdit}
							onEdit={onEdit}
							onInlineDraftChange={updateInlineDraft}
							onLocate={onLocate}
							onOpenFullEditor={openInlineDraftInFullEditor}
							onRequestDelete={onRequestDelete}
							onSaveInlineEdit={saveInlineEdit}
							onStartInlineEdit={beginInlineEdit}
							onToggleCollapsedGroup={toggleCollapsedGroup}
							readonly={readonly}
						/>
					)}
				</>
			) : null}
		</section>
	);
}
